import React, { useEffect, useState } from 'react';
import { dataService } from '../../../services/dataService';
import { Student, LearningPath, Achievement } from '../../../data/types';
import StudentLayout from '../../layouts/StudentLayout';

// Fun mascot asset (replace with your own if desired)
const Mascot = ({ className = '' }: { className?: string }) => (
  <img
    src="https://cdn.pixabay.com/photo/2017/01/31/13/14/animal-2023924_1280.png"
    alt="Mascot"
    className={`w-28 h-28 md:w-36 md:h-36 rounded-full border-4 border-yellow-300 bg-white shadow-xl ${className}`}
    draggable={false}
  />
);

const PlayerCard = ({ student }: { student: Student }) => (
  <div className="flex items-center gap-4 bg-white bg-opacity-70 rounded-2xl shadow-lg px-6 py-4 border-2 border-blue-200">
    <Mascot className="w-20 h-20" />
    <div>
      <div className="text-2xl font-extrabold text-purple-700 drop-shadow">Hi {student.name}!</div>
      <div className="text-lg text-blue-600 font-bold">Grade {student.grade}</div>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-blue-400 font-bold text-lg">{student.totalPoints}</span>
        <span className="text-xs text-blue-700 font-semibold bg-blue-100 rounded px-2 py-0.5 ml-1">XP</span>
      </div>
    </div>
  </div>
);

const ActionButton = ({ label, icon, color, onClick }: { label: string; icon: React.ReactNode; color: string; onClick?: () => void }) => (
  <button
    className={`flex flex-col items-center justify-center w-28 h-28 md:w-32 md:h-32 rounded-full shadow-xl text-white font-bold text-lg text-center bg-gradient-to-br ${color} transform hover:scale-110 transition-all border-4 border-white drop-shadow-lg`}
    onClick={onClick}
    style={{ outline: 'none' }}
  >
    <span className="text-4xl mb-2">{icon}</span>
    <span className="leading-tight">{label}</span>
  </button>
);

const QuestCard = ({ path }: { path: LearningPath }) => (
  <div className="relative bg-white rounded-2xl shadow-lg p-5 flex flex-col gap-2 border-2 border-purple-200 min-w-[240px] max-w-xs hover:scale-105 transition-transform">
    <div className="absolute -top-3 right-4 bg-yellow-400 text-xs font-bold rounded-full px-3 py-0.5 shadow border border-yellow-600">NEW</div>
    <div className="font-bold text-lg text-purple-700 mb-1 flex items-center gap-2">
      <span role="img" aria-label="quest">🗺️</span> {path.name}
    </div>
    <div className="text-sm text-gray-600 mb-2">{path.description}</div>
    <div className="flex gap-2">
      <span className="bg-blue-100 text-blue-700 rounded px-2 py-1 text-xs">Grade {path.grade}</span>
    </div>
    <button className="mt-3 bg-gradient-to-r from-pink-400 to-purple-400 text-white px-4 py-2 rounded-lg font-bold shadow hover:scale-105 transition">Start Adventure</button>
  </div>
);

const AchievementBadge = ({ achievement }: { achievement: Achievement }) => (
  <div className="flex flex-col items-center bg-yellow-50 rounded-xl shadow p-2 mx-2 min-w-[100px] border-2 border-yellow-300 hover:scale-110 transition-transform">
    <div className="text-3xl mb-1">🏆</div>
    <div className="font-bold text-xs text-yellow-800 text-center">{achievement.name}</div>
    <div className="text-xs text-gray-500">+{achievement.points} XP</div>
  </div>
);

const MascotBubble = ({ message }: { message: string }) => (
  <div className="flex items-end gap-2 absolute bottom-6 right-6 z-10">
    <div className="bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-purple-200 text-purple-700 font-semibold text-sm max-w-xs animate-bounce-slow">
      {message}
    </div>
    <Mascot className="w-16 h-16" />
  </div>
);


const StatCard = ({ label, value, icon, color }: { label: string; value: string | number; icon: React.ReactNode; color: string }) => (
  <div className={`flex flex-col items-center p-4 rounded-xl shadow-md bg-gradient-to-br ${color} text-white`}>
    <div className="mb-2 text-3xl">{icon}</div>
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-sm opacity-90">{label}</div>
  </div>
);

const ActionCard = ({ label, icon, color, onClick }: { label: string; icon: React.ReactNode; color: string; onClick?: () => void }) => (
  <button
    className={`flex flex-col items-center justify-center p-4 rounded-xl shadow-md bg-gradient-to-br ${color} text-white transition transform hover:scale-105 active:scale-95 min-w-[120px] min-h-[120px]`}
    onClick={onClick}
  >
    <div className="mb-2 text-3xl">{icon}</div>
    <div className="font-bold text-lg">{label}</div>
  </button>
);

const AchievementCard = ({ achievement }: { achievement: Achievement }) => (
  <div className="flex flex-col items-center bg-white rounded-xl shadow p-2 mx-2 min-w-[120px] border-2 border-yellow-300">
    <div className="text-3xl mb-1">🏅</div>
    <div className="font-bold text-sm text-yellow-700">{achievement.name}</div>
    <div className="text-xs text-gray-500">+{achievement.points} pts</div>
  </div>
);

const StudentDashboardV2: React.FC = () => {
  const [student, setStudent] = useState<Student | null>(null);
  const [learningPaths, setLearningPaths] = useState<LearningPath[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const students = await dataService.getStudents();
        const currentStudent = students[0];
        setStudent(currentStudent);
        const allPaths = await dataService.getLearningPaths();
        const assignedPaths = allPaths.filter(path => path.assignedTo.some(
          assignment => assignment.assignedToType === 'Student' && assignment.assignedToId === currentStudent.id
        ));
        setLearningPaths(assignedPaths);
        const allAchievements = await dataService.getAchievements();
        setAchievements(allAchievements.slice(0, 8));
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex justify-center items-center h-screen text-4xl text-primary animate-bounce">Loading your adventure...</div>;
  if (!student) return <div className="text-center p-8">Student not found.</div>;

  return (
    <StudentLayout>
      <div className="relative min-h-screen bg-gradient-to-br from-yellow-100 via-purple-100 to-blue-100 p-2 md:p-6 overflow-x-hidden">
        {/* Top greeting and player card */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 mt-2">
          <PlayerCard student={student} />
          <div className="hidden md:block flex-1"></div>
        </div>

        {/* Main Actions HUD */}
        <div className="flex flex-wrap justify-center gap-8 mb-12">
          <ActionButton label="Continue" icon={<span>▶️</span>} color="from-green-400 to-lime-400" />
          <ActionButton label="Practice Zone" icon={<span>🎯</span>} color="from-blue-400 to-cyan-400" />
          <ActionButton label="My Progress" icon={<span>📈</span>} color="from-purple-400 to-pink-400" />
          <ActionButton label="Leaderboard" icon={<span>🏆</span>} color="from-yellow-400 to-orange-400" />
        </div>

        {/* Quest Board */}
        <div className="mb-12">
          <div className="text-xl font-extrabold text-blue-700 mb-3 flex items-center gap-2">
            <span className="text-2xl">🎮</span> Your Quests
          </div>
          <div className="flex gap-6 overflow-x-auto pb-2">
            {learningPaths.length === 0 && <div className="text-gray-500 italic">No quests assigned yet!</div>}
            {learningPaths.map(path => (
              <QuestCard key={path.id} path={path} />
            ))}
          </div>
        </div>

        {/* Trophy Shelf */}
        <div className="mb-24">
          <div className="text-xl font-extrabold text-yellow-700 mb-3 flex items-center gap-2">
            <span className="text-2xl">🏆</span> Your Trophy Shelf
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {achievements.length === 0 && <div className="text-gray-500 italic">No trophies yet! Play to earn some!</div>}
            {achievements.map(a => <AchievementBadge key={a.id} achievement={a} />)}
          </div>
        </div>

        {/* Mascot encouragement bubble */}
        <MascotBubble message="Keep going, math hero! Every quest brings you closer to the next level!" />
      </div>
    </StudentLayout>
  );
};


export default StudentDashboardV2;
